/**
 * `src/lib/content/types.ts` — the shapes of the emitted corpus, the render
 * model every surface draws the Qur'an from, and the typed failures.
 * SPEC §1.2 (`content/{loader.ts,types.ts}`), §1.5, §3.1, §3.2.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS MODULE IS NOT FROZEN. THE THING IT DESCRIBES IS.
 *
 * `src/lib/anchor/**` is the frozen contract; this module is the only supported
 * way to get from a byte of `static/content/quran/**` to a word index in that
 * contract. Nothing in here re-derives a segmentation, a fold, a hash or a word
 * count: every one of those comes out of `$lib/anchor`, and this module's job is
 * to attach the corpus's verbatim characters to the frozen module's indices and
 * to REFUSE, loudly, when the two disagree.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * THE ONE RULE THAT MATTERS (M1's pipeline note, and SPEC §3.1's basmala note):
 *
 *   Canonical word index 0 of an ayah is the first word AFTER the prepended
 *   basmala on the 112 rows that carry `b`.
 *
 * A raw `t.split(/\s+/)` is wrong twice over — it counts the 4,379 standalone
 * waqf and sajdah marks as words, and it counts the prepended basmala as words
 * 0..3 of 112 ayat. Either mistake mints `ref` strings that name the wrong words
 * forever, in every backup file the user ever exports. Hence `RenderSegment`:
 * the model is a partition of the verbatim text into anchorable words, the
 * non-anchorable basmala prefix and the non-anchorable standalone marks, so a
 * renderer cannot accidentally index into the wrong one.
 */

/* Relative, not `$lib/anchor`: this module must be importable by a plain node
 * process and by a worker with no SvelteKit alias resolution in scope. */
import type { AyahId, Pos } from '../anchor';

/* ═══════════════════ 1. the emitted corpus, as it is on disk ═════════════ */

/**
 * One row of a `uthmani/juz-NN.json` `ayat` array, exactly as emitted by
 * `pipeline/build-quran.ts`.
 *
 * `w` is the CANONICAL word count — the basmala excluded, the standalone marks
 * excluded — and it is the same number `wordCountOf(i)` reads out of the frozen
 * fixture. `b` is present only on the 112 rows whose `t` opens with a prepended
 * basmala, and it counts SEGMENT tokens.
 */
export interface AyahRow {
	/** `ayahId`, 1..6236. */
	i: AyahId;
	/** surah, 1..114. */
	s: number;
	/** ayah within the surah, 1.. */
	a: number;
	/** the verbatim Tanzil text, marks and all. Never modified. */
	t: string;
	/** canonical word count. */
	w: number;
	/** segment tokens of the prepended basmala — 4, on 112 rows, else absent. */
	b?: number;
}

/** The `{surah, ayah, ayah_id}` triples the emitted files use for endpoints. */
export interface CorpusLocation {
	surah: number;
	ayah: number;
	ayah_id: AyahId;
}

/** The provenance block every emitted file carries. */
export interface CorpusSource {
	file: string;
	sha256: string;
	served_verbatim_at?: string;
}

/**
 * The version triple every emitted file carries, and the reason `versions.ts`
 * exists. A file whose `anchor_space_version` is not the one the frozen wordmap
 * was built under describes a DIFFERENT segmentation, and rendering it would
 * hand the user word indices that do not mean what their `ref` says.
 */
export interface CorpusVersions {
	corpus_version: string;
	anchor_space_version: string;
	segment_version: string;
	numbering: string;
}

/** One `uthmani/juz-NN.json` file. */
export interface JuzFile extends CorpusVersions {
	juz: number;
	start: CorpusLocation;
	end: CorpusLocation;
	ayat_count: number;
	words: number;
	source: CorpusSource;
	_notice: string;
	ayat: AyahRow[];
}

/** One row of `meta.json`'s `surahs` table. */
export interface SurahMeta {
	/** surah number, 1..114. */
	n: number;
	/** ayat in this surah. */
	ayat: number;
	/** `ayahId` of ayah 1. */
	first_ayah_id: AyahId;
	/** `ayahId` of the last ayah. */
	last_ayah_id: AyahId;
	/** cumulative ayat before this surah — `AYAH_OFFSET[n]`. */
	offset: number;
	/** the juz this surah opens in. */
	juz: number;
	/** 1 iff ayah 1 carries a prepended basmala. Exactly 112 rows carry 1. */
	basmala: 0 | 1;
}

/** One row of `meta.json`'s `juz` table. */
export interface JuzMeta {
	juz: number;
	/** `"1:1"` — display form. The authority is `start_ayah_id`. */
	start: string;
	end: string;
	start_ayah_id: AyahId;
	end_ayah_id: AyahId;
	ayat: number;
	words: number;
	/** path relative to `/content/quran/`, e.g. `uthmani/juz-01.json`. */
	file: string;
}

/** One row of `meta.json`'s `sajda` table. */
export interface SajdaMeta {
	surah: number;
	ayah: number;
	ayah_id: AyahId;
}

/** The tokenisation block — documentation the pipeline emits about itself. */
export interface TokenisationMeta {
	rule: string;
	basmala: string;
	segment_version: string;
	word_code_points: string[];
	dropped_standalone_marks: string[];
	ayat_containing_dropped_tokens: number;
}

/** `meta.json`. */
export interface QuranMeta extends CorpusVersions {
	total_ayat: number;
	total_words: number;
	max_words_in_ayah: number;
	max_words_at: string;
	ayah_offset: number[];
	surahs: SurahMeta[];
	juz: JuzMeta[];
	sajda: SajdaMeta[];
	tokenisation: TokenisationMeta;
	source: CorpusSource;
	_notice: string;
}

/* ═══════════════════ 2. the render model ═════════════════════════════════ */

/**
 * What a segment of an ayah IS, from the point of view of anchoring:
 *
 *   `word`    — a canonical word. ANCHORABLE. Carries its 0-based canonical
 *               index and its `Pos`.
 *   `basmala` — the prepended basmala of the 112 rows. NOT ANCHORABLE, and
 *               every mushaf renderer draws it as a separate centred header
 *               rather than as the opening of ayah 1 (SPEC §3.1's note).
 *   `mark`    — a standalone waqf or sajdah sign. NOT ANCHORABLE. It must still
 *               be RENDERED — dropping it would change the text on screen — so
 *               it is in the model, just not in the word array.
 */
export type SegmentKind = 'word' | 'basmala' | 'mark';

/** Fields shared by every segment. Offsets are into the row's verbatim `t`. */
interface SegmentBase {
	kind: SegmentKind;
	/** verbatim substring of `t`. */
	text: string;
	/** inclusive start offset in `t` (UTF-16 code units). */
	start: number;
	/** exclusive end offset in `t`. */
	end: number;
}

/**
 * A canonical word — the ONLY anchorable segment kind.
 *
 * `index` is the canonical index the anchor space addresses: `hafs:2:1:0-1`
 * names the segment with `index === 0`, which on 2:1 is الٓمٓ and not بِسْمِ.
 */
export interface WordSegment extends SegmentBase {
	kind: 'word';
	anchorable: true;
	/** 0-based canonical word index, `0 .. w-1`. */
	index: number;
	/** `posOf(ayahId, index)` — Layer 2 of the anchor. */
	pos: Pos;
}

/** The prepended basmala, as one segment. Never anchorable. */
export interface BasmalaSegment extends SegmentBase {
	kind: 'basmala';
	anchorable: false;
	/** the verbatim tokens it is made of — four, on all 112 rows. */
	tokens: readonly string[];
}

/** A standalone waqf or sajdah mark. Never anchorable. */
export interface MarkSegment extends SegmentBase {
	kind: 'mark';
	anchorable: false;
}

/** A segment of the render model. */
export type RenderSegment = WordSegment | BasmalaSegment | MarkSegment;

/**
 * The render model of one ayah: the corpus row, partitioned.
 *
 * INVARIANTS, all asserted by `buildAyahModel` at runtime and by
 * `content.spec.ts` over all 6,236 ayat:
 *
 *   words.length === row.w === wordCountOf(ayahId)
 *   words[k].index === k                                for every k
 *   words[k].pos   === posOf(ayahId, k)                 for every k
 *   words.map(text) deep-equals canonicalWords(s, a, t)
 *   basmala !== null  ⇔  row.b is set
 *   basmala.tokens.map(fold) === BASMALA_FOLDED_WORDS
 *   segments are in source order, non-overlapping, and reassemble to `t`
 */
export interface AyahModel {
	ayahId: AyahId;
	surah: number;
	ayah: number;
	/** the verbatim text, byte-for-byte as emitted. */
	text: string;
	/** every segment, in source order — words, marks and the basmala. */
	segments: readonly RenderSegment[];
	/** the anchorable words only, indices `0 .. wordCount-1`, in order. */
	words: readonly WordSegment[];
	/** `words.length`. Equals `row.w` and `wordCountOf(ayahId)`. */
	wordCount: number;
	/** the prepended basmala, or `null` on the 6,124 rows without one. */
	basmala: BasmalaSegment | null;
}

/* ═══════════════════ 3. typed failures ═══════════════════════════════════ */

/**
 * Base of every failure this module raises.
 *
 * A `code` and not just a message: the Repair screen (SPEC §3.2 tier 4) and the
 * diagnostics ring buffer both need to branch on WHAT went wrong without
 * pattern-matching English prose, and these strings are the stable identifiers
 * for that. Add to the union; do not rename a member.
 */
export type ContentErrorCode =
	| 'fetch_failed'
	| 'http_error'
	| 'malformed_json'
	| 'malformed_shape'
	| 'unknown_juz'
	| 'unknown_surah'
	| 'unknown_ayah'
	| 'version_mismatch'
	| 'corpus_version_mismatch'
	| 'word_count_mismatch'
	| 'tokeniser_disagreement'
	| 'basmala_mismatch'
	| 'segmentation_not_lossless'
	| 'word_span_out_of_range';

/** Anything this module refuses to do, with a machine-readable reason. */
export class ContentError extends Error {
	readonly code: ContentErrorCode;
	/** what was being read — a URL, a path, or `ayahId 2:255`. */
	readonly where: string;

	constructor(code: ContentErrorCode, where: string, message: string) {
		super(`${message} (${where})`);
		this.name = 'ContentError';
		this.code = code;
		this.where = where;
	}
}

/** A file could not be fetched, decoded, or did not have the expected shape. */
export class ContentLoadError extends ContentError {
	constructor(code: ContentErrorCode, where: string, message: string) {
		super(code, where, message);
		this.name = 'ContentLoadError';
	}
}

/**
 * The loaded corpus was built under a different anchor space, segmentation or
 * corpus version than the code reading it.
 *
 * THIS IS THE ERROR THAT MUST NEVER BE SWALLOWED. A version mismatch means the
 * word indices in the file and the word indices in `word-counts.json` are not
 * the same coordinate system; rendering anyway would let the user mint a `ref`
 * that points at a word they did not select. SPEC §3.2's renderer gate — "a
 * renderer that cannot supply a wordmap to the canonical segmentation may not
 * render word anchors" — is this class being thrown rather than caught.
 */
export class CorpusVersionError extends ContentError {
	readonly expected: string;
	readonly actual: string;
	readonly field: string;

	constructor(
		code: ContentErrorCode,
		where: string,
		field: string,
		expected: string,
		actual: string
	) {
		super(
			code,
			where,
			`${field} mismatch: the frozen anchor space expects ${JSON.stringify(expected)}, ` +
				`this file declares ${JSON.stringify(actual)}`
		);
		this.name = 'CorpusVersionError';
		this.field = field;
		this.expected = expected;
		this.actual = actual;
	}
}

/**
 * The corpus and the frozen anchor space disagree about an ayah's words.
 *
 * Distinct from `CorpusVersionError` on purpose: the versions agreed, so this is
 * not "an older build" — it is a build whose content does not match its own
 * declared segmentation, which is a pipeline defect and not a migration.
 */
export class WordSpanContractError extends ContentError {
	constructor(code: ContentErrorCode, where: string, message: string) {
		super(code, where, message);
		this.name = 'WordSpanContractError';
	}
}
